import { RHYTHM } from '@/config/training';
import type { BeatHit, TimingVerdict } from '@/types/training';
import { clamp, median } from '@/audio/dsp/stats';

interface Expectation {
  index: number;
  time: number;
  onsetTime: number | null;
}

/**
 * Matches detected onsets against scheduled beats.
 *
 * ## Why the raw error is not the answer
 *
 * The delay between a string being struck and the samples reaching the analyser
 * is unknown and device-dependent: roughly 30 ms on a desktop, 100–300 ms on a
 * phone, worse over Bluetooth. Telling a beginner "you were 180 ms late" when
 * 170 ms of that is their hardware would be a lie, and an actively harmful one —
 * they would start rushing to compensate.
 *
 * So the matcher estimates the **systematic offset** as the median of the raw
 * errors (clamped, so it cannot swallow genuine dragging) and reports every
 * error relative to it. What is measured is therefore *steadiness*: whether the
 * player's hits are evenly spaced against the click. That is exactly the skill
 * a rhythm exercise should build, and it is what the UI says it measures.
 */
export class TimingMatcher {
  private readonly pending: Expectation[] = [];
  private readonly rawErrors: number[] = [];
  private readonly resolved: BeatHit[] = [];
  /**
   * Onsets that arrived before any expectation could claim them.
   *
   * Expectations are created when a beat *fires*, but an early hit is played —
   * and detected — before its beat exists. Dropping those silently scored every
   * deliberately-early player as "missed" while claiming to measure their
   * timing. The buffer holds recent orphan onsets so `expect()` can claim one
   * retroactively.
   */
  private readonly unmatched: number[] = [];
  /**
   * Extra time a beat stays pending after its window closes.
   *
   * The guitar gate confirms an onset only after it has seen the post-attack
   * evidence, so a legitimate hit arrives up to `GATE.eventDecisionSec` after
   * it was struck — with its *original* timestamp. Without this lag, a beat
   * could resolve as "missed" moments before its confirmed hit arrived. The
   * lag delays only the verdict's appearance, never the measured error.
   */
  private resolveLagSec = 0;

  constructor(private hitWindowSec: number) {}

  setBeatInterval(beatIntervalSec: number): void {
    this.hitWindowSec = Math.min(
      beatIntervalSec * RHYTHM.hitWindowFraction,
      RHYTHM.hitWindowMaxMs / 1000,
    );
  }

  setResolveLag(seconds: number): void {
    this.resolveLagSec = Math.max(0, seconds);
  }

  expect(index: number, time: number): void {
    const expectation: Expectation = { index, time, onsetTime: null };
    // Claim the closest already-heard onset inside the window, if one exists.
    let bestIndex = -1;
    let bestDistance = Infinity;
    for (let i = 0; i < this.unmatched.length; i++) {
      const distance = Math.abs(this.unmatched[i] - time);
      if (distance <= this.hitWindowSec && distance < bestDistance) {
        bestDistance = distance;
        bestIndex = i;
      }
    }
    if (bestIndex >= 0) {
      expectation.onsetTime = this.unmatched[bestIndex];
      this.unmatched.splice(bestIndex, 1);
    }
    this.pending.push(expectation);
  }

  /** Attribute an onset to the closest unmatched expectation, if any is close. */
  addOnset(time: number): void {
    let best: Expectation | null = null;
    let bestDistance = Infinity;
    for (const expectation of this.pending) {
      if (expectation.onsetTime !== null) continue;
      const distance = Math.abs(time - expectation.time);
      if (distance <= this.hitWindowSec && distance < bestDistance) {
        best = expectation;
        bestDistance = distance;
      }
    }
    if (best) {
      best.onsetTime = time;
      return;
    }
    // No beat wants it yet — it may be early for a beat that has not fired.
    this.unmatched.push(time);
    const cutoff = time - 2;
    while (this.unmatched.length > 0 && this.unmatched[0] < cutoff) this.unmatched.shift();
  }

  /**
   * Resolve every expectation whose window has closed.
   * @returns the hits resolved by this call, in order.
   */
  collect(now: number): BeatHit[] {
    const done: BeatHit[] = [];
    while (
      this.pending.length > 0 &&
      this.pending[0].time + this.hitWindowSec + this.resolveLagSec < now
    ) {
      const expectation = this.pending.shift();
      if (!expectation) break;
      if (expectation.onsetTime !== null) {
        this.rawErrors.push(expectation.onsetTime - expectation.time);
      }
      const hit = this.toHit(expectation);
      this.resolved.push(hit);
      done.push(hit);
    }
    return done;
  }

  /** Current estimate of the device's systematic input latency, in ms. */
  get latencyOffsetMs(): number {
    if (this.rawErrors.length < RHYTHM.latencyEstimateHits) return 0;
    const offset = median(this.rawErrors) * 1000;
    return clamp(offset, -RHYTHM.latencyClampMs, RHYTHM.latencyClampMs);
  }

  /**
   * Score over everything resolved so far, 0..100, recomputed with the *final*
   * latency estimate rather than the running one.
   */
  score(): number {
    if (this.resolved.length === 0) return 0;
    const offset = this.latencyOffsetMs;
    let total = 0;
    for (const hit of this.resolved) {
      if (hit.onsetTime === null) continue;
      const error = Math.abs((hit.onsetTime - hit.beatTime) * 1000 - offset);
      total += 1 - Math.min(1, error / RHYTHM.zeroScoreMs);
    }
    return Math.round((total / this.resolved.length) * 100);
  }

  /** Hits recomputed against the final offset — used for the results screen. */
  finalHits(): BeatHit[] {
    const offset = this.latencyOffsetMs;
    return this.resolved.map((hit) => {
      if (hit.onsetTime === null) return hit;
      const errorMs = (hit.onsetTime - hit.beatTime) * 1000 - offset;
      return { ...hit, errorMs, verdict: verdictFor(errorMs) };
    });
  }

  get counts(): Record<TimingVerdict, number> {
    const result: Record<TimingVerdict, number> = { early: 0, onTime: 0, late: 0, missed: 0 };
    for (const hit of this.finalHits()) result[hit.verdict]++;
    return result;
  }

  /**
   * Forget beats that have been scheduled but not yet resolved.
   *
   * Called when the exercise pauses. Those beats are no longer coming — the
   * metronome has stopped — and letting their windows lapse would score a
   * handful of "missed" hits against a player who simply pressed pause.
   */
  dropPending(): void {
    this.pending.length = 0;
    // A strum played just before the pause must not resurface as an "early hit"
    // for the first beat after the resume's count-in.
    this.unmatched.length = 0;
  }

  reset(): void {
    this.pending.length = 0;
    this.rawErrors.length = 0;
    this.resolved.length = 0;
    this.unmatched.length = 0;
  }

  private toHit(expectation: Expectation): BeatHit {
    if (expectation.onsetTime === null) {
      return {
        beatIndex: expectation.index,
        beatTime: expectation.time,
        onsetTime: null,
        errorMs: null,
        verdict: 'missed',
      };
    }
    const errorMs = (expectation.onsetTime - expectation.time) * 1000 - this.latencyOffsetMs;
    return {
      beatIndex: expectation.index,
      beatTime: expectation.time,
      onsetTime: expectation.onsetTime,
      errorMs,
      verdict: verdictFor(errorMs),
    };
  }
}

export function verdictFor(errorMs: number): TimingVerdict {
  const magnitude = Math.abs(errorMs);
  if (magnitude <= RHYTHM.onTimeMs) return 'onTime';
  if (magnitude <= RHYTHM.closeMs) return errorMs < 0 ? 'early' : 'late';
  return errorMs < 0 ? 'early' : 'late';
}
